"use client";
import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "utils";
import { PortfolioType } from "../../Portfolio/types";
import Translations from "@translate/components/CoPilot/CopilotHeader/CoPilotTab";
import { I18nUtil } from "@utils/i18n";
import { ROUTER_PATH } from "@/src/config/router";
import { usePortfolioType } from "@/src/hooks/usePortfolioType";

const I18n = I18nUtil.createScoped(Translations);

export interface CoPilotTabProps {
  className?: string;
}

export const CoPilotTab: React.FC<CoPilotTabProps> = (props) => {
  const { className } = props;
  const tabIndicatorRef = useRef<HTMLDivElement>(null);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const { switchType, currentType } = usePortfolioType();

  // 获取收藏状态数据 - 观察整个 state 对象

  // 动态计算 portfolioId 根据资产情况
  const portfolioTabs = [
    {
      id: PortfolioType.EXTERNAL,
      label: I18n.t("Portfolio"),
      link: ROUTER_PATH.CO_PILOT_PORTFOLIO,
    },
    {
      id: PortfolioType.WATCHLIST,
      label: I18n.t("Watchlist"),
      link: ROUTER_PATH.CO_PILOT_PORTFOLIO,
    },
  ];

  const checkScrollable = useCallback(() => {
    const element = tabIndicatorRef.current;
    if (!element) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = element;
    // 判断是否还能向右滚动（右侧还有内容）
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1; // 使用 1px 容差避免浮点数精度问题
    setShowRightShadow(canScrollRight);
  }, []);

  useEffect(() => {
    const element = tabIndicatorRef.current;
    if (!element) {
      return;
    }

    // 初始检查
    checkScrollable();

    // 监听滚动事件
    element.addEventListener("scroll", checkScrollable);

    // 监听窗口大小变化（可能影响可滚动性）
    const resizeObserver = new ResizeObserver(() => {
      checkScrollable();
    });
    resizeObserver.observe(element);

    // 监听内容变化（MutationObserver 可选，用于处理动态内容变化）
    const mutationObserver = new MutationObserver(() => {
      // 延迟检查，确保 DOM 更新完成
      setTimeout(checkScrollable, 0);
    });
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    return () => {
      element.removeEventListener("scroll", checkScrollable);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [checkScrollable]);

  return (
    <div
      className={cn(
        "flex justify-between items-end w-full border-b border-border_white_transparent10 md:px-15 md:h-17",
        className,
      )}
    >
      <div className="relative flex-1">
        <div
          className={cn(
            "absolute right-0 top-0 bottom-0 w-5 z-10 pointer-events-none bg-gradient-to-l from-[#1A1A1A] to-transparent transition-opacity duration-200",
            showRightShadow ? "opacity-100" : "opacity-0",
          )}
        />
        <tab-indicator
          ref={tabIndicatorRef}
          className="flex gap-6 overflow-x-auto no-scrollbar [--underline-color:var(--edgen-border\_brand\_solid)] [--animation-duration:0.2s] self-end relative"
          effect="underline"
          data-tracking-scope="tabs"
        >
          {portfolioTabs.map((item) => {
            const isActive = item.id === currentType;
            return (
              <p
                data-openreplay-label={`CopilotHeader · CoPilotTab · p | ${item.label}`}
                key={item.id}
                role="tab-item"
                data-actived={isActive}
                // data-disabled={!disabled || false}
                className={cn(
                  "tab_item flex-shrink-0 py-2 title_l text-content_tertiary",
                  { "text-content_primary": isActive },
                )}
                onClick={() => switchType(item.id)}
              >
                {item.label}
              </p>
            );
          })}
        </tab-indicator>
      </div>
    </div>
  );
};
